import { getSessionOrganizationId } from '../../../../shell/session/sessionOrganization';
import { buildCsvFromRows } from '../../../../shared/csv-core/infra/buildCsv';
import { getCatalogRamSnapshotCache } from '../../../../catalog/infra/catalogRamSnapshotCache';
import { normalizeUnitOfMeasure } from '@shared/quantity';
import {
  STOCK_CSV_EXPORT_HEADERS,
  STOCK_CSV_IMPORT_HEADERS,
  stockCsvTemplateExampleRows,
} from '../../domain/csv/stockCsvSchemas';

type StockCsvExportRow = {
  sku: string;
  name: string;
  unitOfMeasure: string | null;
  quantity: number;
  minQuantity: number;
  avgCost: number;
  supplier: string | null;
  expiresAt: string | null;
};

function toCsvLine(r: StockCsvExportRow): string[] {
  return [
    r.sku,
    r.name ?? '',
    normalizeUnitOfMeasure(r.unitOfMeasure),
    String(r.quantity),
    String(r.minQuantity),
    String(r.avgCost),
    r.supplier ?? '',
    r.expiresAt ?? '',
  ];
}

/** Export desde el snapshot en memoria (web sin SQLite). */
export function collectStockCsvExportRowsFromWebCache(
  branchId: string,
  organizationId: string
): StockCsvExportRow[] {
  const cache = getCatalogRamSnapshotCache();
  if (!cache) return [];

  const supplierName = new Map(
    (cache.suppliers ?? []).map((s) => [String(s.id), String(s.name ?? '')])
  );
  const stockByProduct = new Map<string, (typeof cache.stocks)[number]>();
  for (const s of cache.stocks ?? []) {
    if (String(s.branch_id) !== branchId) continue;
    stockByProduct.set(String(s.product_id), s);
  }

  const rows: StockCsvExportRow[] = [];
  for (const p of cache.products ?? []) {
    if (p.organization_id && String(p.organization_id) !== organizationId) continue;
    if (p.is_misc === true) continue;
    if (p.item_type === 'service') continue;
    if (p.is_active === false) continue;
    const sku = String(p.sku ?? '').trim();
    if (!sku) continue;
    const stock = stockByProduct.get(String(p.id));
    const supplierId = p.preferred_supplier_id != null ? String(p.preferred_supplier_id) : '';
    rows.push({
      sku,
      name: String(p.name ?? ''),
      unitOfMeasure: p.unit_of_measure != null ? String(p.unit_of_measure) : null,
      quantity: stock?.quantity ?? 0,
      minQuantity: stock?.min_quantity ?? 0,
      avgCost: stock?.avg_cost ?? 0,
      supplier: supplierId ? (supplierName.get(supplierId) ?? '') : '',
      expiresAt: stock?.expires_at ?? null,
    });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  return rows;
}

/**
 * Snapshot del estado actual de la sucursal: todos los productos físicos activos con SKU,
 * cantidad y costo promedio (0 si no hay fila). No es un "ingreso": al reimportar, el CSV de
 * stock SUMA cantidades (ver stockCsvImport), así que este export sirve para consulta, no para
 * reimportar tal cual.
 */
export async function buildStockCsvExport(branchId: string): Promise<string> {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) {
    return buildCsvFromRows([...STOCK_CSV_EXPORT_HEADERS], []);
  }

  const rows = collectStockCsvExportRowsFromWebCache(branchId, organizationId);
        return buildCsvFromRows([...STOCK_CSV_EXPORT_HEADERS], rows.map(toCsvLine));
}

/**
 * Plantilla de ingreso (mismas columnas que "Agregar stock").
 * Damos un ejemplo por cada `motivo` posible para que se entienda qué valores son válidos.
 * `proveedor` acepta el nombre, el código o el id de un proveedor existente.
 */
export function buildStockCsvTemplate(): string {
  return buildCsvFromRows([...STOCK_CSV_IMPORT_HEADERS], stockCsvTemplateExampleRows());
}
